<template>
  <div>
    <v-card-title>
      <h2>Contacts</h2>
      <v-spacer/>
      <v-btn
        v-if="checkPermission('contacts', PERMISSIONS.CAN_BULK)"
        :to="{name: 'ContactImport'}"
        round
        color="yellow"
      >
        <v-icon
          dark
          left
        >
          mdi-database-import
        </v-icon>
        Import
      </v-btn>
      <v-btn
        v-if="checkPermission('contacts', PERMISSIONS.CAN_CREATE)"
        dark
        round
        color="green"
        @click="newContact = true"
      >
        <v-icon dark>add</v-icon>
        New Contact
      </v-btn>
    </v-card-title>
    <br />
    <v-layout
      row wrap
    >
      <v-flex
        sm12
        md3
        style="padding:0 5px 0 5px;"
      >
        <v-card>
          <v-toolbar
            class="primary-color"
            dense
            flat
            dark
          >
            Search &amp; Filtering
          </v-toolbar>
          <v-card-text>
            <v-text-field
              v-model.trim="searchText"
              append-icon="search"
              label="Search Text"
              single-line
              hide-details
              v-on:keyup.enter="search"
            />
            <v-select
              v-model="selectedField"
              :items="searchFields"
            />
            <v-combobox
              v-model.trim="filterTags"
              :items="tagResults"
              :search-input.sync="tagSearch"
              label="Search by Tag"
              item-text="tag"
              item-value="tag"
              :return-object="false"
              flat
              small-chips
              hide-selected
              auto-select-first
              @input="tagSearch = ''"
            />
            <v-radio-group
              style="margin:0;"
              v-model="filterEnabled"
            >
              <template v-slot:label>
                <div>Filter by Status</div>
              </template>
              <v-layout
                row wrap
              >
                <v-flex
                  sm6
                >
                  <v-radio
                    value="true"
                  >
                    <template v-slot:label>
                      <v-icon
                        left
                      >
                        person
                      </v-icon>
                      Enabled
                    </template>
                  </v-radio>
                </v-flex>
                <v-flex
                  sm6
                >
                  <v-radio
                    value="false"
                  >
                    <template v-slot:label>
                      <v-icon
                        left
                      >
                        mdi-account-off-outline
                      </v-icon>
                      Disabled
                    </template>
                  </v-radio>
                </v-flex>
              </v-layout>
            </v-radio-group>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn
              @click="searchText='';tagSearch = '';filterTags=null;filterEnabled=null;search()"
            >
              Clear
            </v-btn>
            <v-btn
              color="primary"
              @click="search"
            >
              Search
            </v-btn>
            <v-spacer/>
          </v-card-actions>
        </v-card>
      </v-flex>
      <v-flex
        sm12
        md9
      >
        <v-card>
          <v-data-table
            :items="contacts"
            :headers="headers"
            :pagination.sync="pagination"
            :total-items="totalContacts"
            :rows-per-page-items="paginationStrings"
            :loading="loading"
            disable-initial-sort
          >
            <template slot="no-data">
              <tr>
                <td colspan="6">
                  <div style="text-align:center;padding:25px;">
                    <v-icon size="125px">find_in_page</v-icon>
                    <h3>No Results</h3>
                  </div>
                </td>
              </tr>
            </template>
            <template
              slot="items"
              slot-scope="props"
            >
              <tr
                :style="(props.item.tags.length > 0) ? 'border-bottom:0;' : null"
              >
                <td>
                  <v-icon>
                    {{props.item.is_active ? 'person' : 'mdi-account-off-outline'}}
                  </v-icon>
                </td>
                <td>
                  <router-link :to="{ name: 'Contact', params: { id: props.item.id } }">
                    <b>{{ props.item.cuid }}</b>
                  </router-link>
                </td>
                <td>
                  <router-link :to="{ name: 'Contact', params: { id: props.item.id } }">
                    <b>{{ props.item.last_name }}</b>
                  </router-link>
                </td>
                <td>
                  {{ props.item.first_name }}
                </td>
                <td>
                  {{ props.item.middle_name }}
                </td>
                <td class="justify-center layout px-0">
                  <v-icon
                    small
                    class="mr-2"
                    @click="$router.push({ name: 'Contact', params: { id: props.item.id } })"
                  >
                    edit
                  </v-icon>
                </td>
              </tr>
              <tr
                v-if="props.item.tags.length > 0"
              >
                <td colspan="6">
                  <span
                    v-for="tag in resortTags(props.item.tags)"
                    :key="tag"
                  >
                    <v-chip
                      label
                      small
                      :color="(tag === filterTags) ? 'yellow' : null"
                      :outline="tag.includes(':')"
                    >
                      {{tag}}
                    </v-chip>
                  </span>
                </td>
              </tr>
            </template>
            <template slot="actions-append">
              <v-tooltip left>
                <span
                  slot="activator"
                >
                  <v-btn
                    round
                    flat
                    fab
                    small
                    @click="downloadCSV()"
                  >
                    <v-icon
                      color="green"
                    >
                      table_chart
                    </v-icon>
                  </v-btn>
                </span>
                <span>Download CSV<br /><small>Of Current Search Terms</small></span>
              </v-tooltip>
              <v-spacer />
            </template>
          </v-data-table>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog
      v-model="downloadingCSV"
      persistent
      width="300"
    >
      <v-card
        color="white"
      >
        <v-card-text>
          Generating CSV File...<br/>
          <small>Depending on your query, this may take some time.</small>
          <v-progress-linear
            indeterminate
            color="blue"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <ContactDialog
      v-if="newContact"
      @input="newContact = false;$router.push({ name: 'Contact', params: { id: arguments[0].id } })"
      @cancel="newContact = false"
    />
  </div>
</template>

<script>
import api from '@/api'
import pagination from '@/components/global/pagination'
import ContactDialog from '@/components/admin/contacts/ContactDialog'

const SEARCH_FIELDS = ['Any', 'CUID', 'Email','Last Name', 'First Name', 'Middle Name', 'Phone Number']

export default {
  name: 'ContactList',
  components: {
    ContactDialog,
  },
  mixins: [
    pagination
  ],
  data () {
    return {
      contacts: [],
      pagination: {},
      searchText: '',
      loading: true,
      paginationStrings: [
        10, 25, 50
      ],
      headers: [
        { text: '', value: '', sortable: false, width: 50 },
        { text: 'CUID', value: 'cuid' },
        { text: 'Last Name', value: 'last_name' },
        { text: 'First Name', value: 'first_name' },
        { text: 'Middle Name', value: 'middle_name' },
        {
          text: 'Actions', align: 'center', sortable: false
        }
      ],
      filter: {},
      filterTags: null,
      filterEnabled: null,
      tagSearch: null,
      tagResults: [],
      newContact: false,
      downloadingCSV: false,
      searchFields: SEARCH_FIELDS,
      selectedField: SEARCH_FIELDS[0]
    }
  },
  created () {
    this.documentTitle('Contacts')
  },
  computed: {
    totalContacts () {
      return this.pagination.totalItems
    }
  },
  watch: {
    tagSearch () {
      if (this.tagSearch) {
        this.fetchTags()
      }
    }
  },
  methods: {
    fetch () {
      this.loading = true
      api.userform.contact.list({
        search: this.searchText, 
        ordering: (this.pagination.descending ? '-' : '') + this.pagination.sortBy, 
        pageNum: this.pagination.page, 
        count: this.pagination.rowsPerPage, 
        filter: this.filter || null, 
        searchField: this.selectedField
      })
        .then(data => {
          this.contacts = data.results
          this.pagination.page = data.page_number
          this.pagination.totalItems = data.count
          this.loading = false
          this.componentCreated = true
        })
    },
    downloadCSV () {
      this.downloadingCSV = true
      api.userform.contact.downloadCsv({search: this.searchText, ordering: (this.pagination.descending ? '-' : '') + this.pagination.sortBy, filter: this.filter || null})
        .then(response => {
          this.downloadingCSV = false
          let blob = new Blob([response.data])
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'Export_Contacts.csv'
          link.click()
        })
    },
    fetchTags () {
      api.userform.contactTags.get(this.tagSearch)
        .then(tagResults => {
          this.tagResults = tagResults
        })
    },
    addContact () {
      this.$router.push({ name: 'Contact' })
    },
    search () {
      this.pagination.page = 1
      var queryParam = {}, filterQueryParam = {}
      if (this.searchText) {
        queryParam['search'] = this.searchText
        if (this.selectedField)
          queryParam['field'] = this.selectedField
      }
      if (this.filterTags) {filterQueryParam['tags__name'] = this.filterTags} else {delete filterQueryParam['tags__name']}
      if (this.filterEnabled) {filterQueryParam['is_active'] = this.filterEnabled} else {delete filterQueryParam['is_active']}
      if (Object.keys(filterQueryParam).length !== 0) {queryParam['filter'] = JSON.stringify(filterQueryParam)}
      this.$router.push({ query: queryParam })
    },
  }
}
</script>

<style>

.search-bar {
  margin-top: 1em;
}

table {
  margin-top: 1em;
}

</style>
